import React from 'react';

import { FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { CloseRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { ISigner } from '../../../../types/Parties';

export interface ISignerProps {
  isOpen: boolean;
  onClose: () => void;
  signersToUpdate: Array<ISigner>;
  submitUpdate: (values: any) => void;
  isLoading: boolean;
}

const formSchema = Yup.object().shape({
  signers: Yup.array().of(
    Yup.object().shape({
      FirstName: Yup.string().required().nullable(),
      LastName: Yup.string().required().nullable(),
      MiddleName: Yup.string().nullable(),
      Suffix: Yup.string().nullable(),
    })
  ),
});

export const SignerUpdateModal: React.FC<ISignerProps> = (
  props: ISignerProps
) => {
  const { isOpen, onClose, signersToUpdate, submitUpdate, isLoading } = props;
  return (
    <Formik
      enableReinitialize
      validateOnChange={true}
      initialValues={{ signers: signersToUpdate }}
      validationSchema={formSchema}
      onSubmit={() => {}}>
      {({ values, isValid, getFieldProps, dirty, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Dialog
            fullWidth
            maxWidth='md'
            open={isOpen}
            scroll='paper'
            aria-labelledby='form-dialog-title'>
            <DialogTitle>
              <Grid
                container
                justifyContent='space-between'
                alignItems='center'>
                <Grid item sx={{ flexGrow: 1, flexShrink: 0 }}>
                  Please review witness information below
                </Grid>
                <Grid item sx={{ flexGrow: 0, flexShrink: 1 }}>
                  <IconButton
                    color='inherit'
                    onClick={onClose}
                    component='button'>
                    <CloseRounded fontSize='inherit' />
                  </IconButton>
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent dividers>
              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '200px',
                  }}>
                  <Typography variant='h6'>
                    {'Identifying witnesses...'}
                  </Typography>
                  <CircularProgress />
                </Box>
              )}
              {!isLoading && (
                <FieldArray
                  name='signers'
                  render={() => (
                    <div>
                      {values.signers.map((signer, index: number) => (
                        <Grid container spacing={2} key={index} mb={2}>
                          <Grid item>
                            <TextField
                              label='First Name'
                              id={`signers[${index}].FirstName`}
                              required
                              variant='standard'
                              {...getFieldProps(`signers[${index}].FirstName`)}
                            />
                          </Grid>
                          <Grid item>
                            <TextField
                              label='Middle Name'
                              id={`signers[${index}].MiddleName`}
                              variant='standard'
                              {...getFieldProps(
                                `signers[${index}]?.MiddleName`
                              )}
                            />
                          </Grid>
                          <Grid item>
                            <TextField
                              label='Last Name'
                              id={`signers[${index}].LastName`}
                              required
                              variant='standard'
                              {...getFieldProps(`signers[${index}].LastName`)}
                            />
                          </Grid>
                          <Grid item>
                            <TextField
                              label='Suffix'
                              id={`signers[${index}].Suffix`}
                              variant='standard'
                              {...getFieldProps(`signers[${index}]?.Suffix`)}
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </div>
                  )}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                onClick={() => submitUpdate(values.signers)}
                type='submit'
                variant='contained'
                color='primary'
                disabled={!(isValid && dirty)}>
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};
